import React, { HTMLAttributes, ReactNode } from 'react';
import { PhotographerApplicationDTO } from 'appAPITypes';
import { FieldArray, FormikProvider } from 'formik';
import { get } from 'lodash';
import { FormikPropsSlice, getFormikError } from 'primitives/input/formikUtils';
import { JoinPageTranslations } from '../joinPageTranslations';
import { ShText } from '@shoootin/translations';
import { FormError } from '../../../primitives/input/formError';
import classnames from 'classnames';
import { ShCol, ShRow } from '@shoootin/components-web';
import { ShInputText } from '@shoootin/components-web';

export type MaterialDroneSectionValues = Pick<
  PhotographerApplicationDTO,
  'hasDrone' | 'droneMaterial'
>;

const RealisationLinkRowLayout = ({
  label,
  input,
  button,
}: {
  label?: ReactNode;
  input: ReactNode;
  button?: ReactNode;
}) => {
  return (
    <ShRow
      className="field-row wrap-s"
      css={{
        '> * + *': {
          marginTop: '1rem',
        },
      }}
    >
      <ShCol xs={60} md={20}>
        {label}
      </ShCol>
      <ShCol xs={60} md={30}>
        {input}
      </ShCol>
      <ShCol xs={60} md={10}>
        {button}
      </ShCol>
    </ShRow>
  );
};

const RealisationTitle = ({ error }: { error?: string }) => {
  return (
    <>
      <label>
        <ShText message={JoinPageTranslations.realisationLinksTitle} />
      </label>
      {error && <FormError error={error} />}
    </>
  );
};

const LinkButton = ({
  children,
  modifier,
  style,
  ...rest
}: {
  children: ReactNode;
  modifier?: 'invert' | 'active' | undefined;
} & HTMLAttributes<HTMLDivElement>) => (
  <div
    css={{ display: 'inline-block', cursor: 'pointer' }}
    className={classnames('link', modifier)}
    {...rest}
  >
    {children}
  </div>
);

export const RealisationLinksRows = <FieldName extends string>({
  arrayName,
  form,
}: {
  arrayName: string;
  form: FormikPropsSlice<keyof MaterialDroneSectionValues>;
}) => {
  const links: string[] = get(form.values, arrayName);
  // TOimprove how to type this better?
  // TOimprove migrate to useFieldArray once available (https://github.com/jaredpalmer/formik/issues/1476)
  // TOimprove the provider is currently mandatory due to using hooks (https://github.com/jaredpalmer/formik/issues/1477)
  return (
    <FormikProvider value={form}>
      <FieldArray name={arrayName}>
        {(arrayHelpers) => {
          return links.map((link, index) => {
            const isFirst = index === 0;
            return (
              <RealisationLinkRowLayout
                key={index}
                label={
                  isFirst && (
                    <RealisationTitle
                      error={getFormikError(form, arrayName)!}
                    />
                  )
                }
                input={
                  <ShInputText
                    placeholder="https://"
                    themeSize={'l'}
                    value={link}
                    onChange={(e) =>
                      arrayHelpers.replace(index, e.target.value)
                    }
                  />
                }
                button={
                  isFirst ? (
                    <LinkButton onClick={() => arrayHelpers.push('')}>
                      <ShText
                        message={JoinPageTranslations.realisationLinksAddLink}
                      />
                    </LinkButton>
                  ) : (
                    <LinkButton onClick={() => arrayHelpers.remove(index)}>
                      <ShText
                        message={
                          JoinPageTranslations.realisationLinksRemoveLink
                        }
                      />
                    </LinkButton>
                  )
                }
              />
            );
          });
        }}
      </FieldArray>
    </FormikProvider>
  );
};
